import * as React from "react";

import { cn } from "@/lib/utils";

const Card = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, style, onMouseEnter, onMouseLeave, ...props }, ref) => {
    const [hovered, setHovered] = React.useState(false);
    const isDark = typeof document !== "undefined" && document.documentElement.classList.contains("dark");
    const fallbackBase = isDark
      ? "0 1px 3px rgba(0, 0, 0, 0.35), 0 6px 12px rgba(0, 0, 0, 0.45)"
      : "0 1px 3px rgba(0, 0, 0, 0.08), 0 6px 12px rgba(0, 0, 0, 0.12)";
    const fallbackHover = isDark
      ? "0 3px 10px rgba(0, 0, 0, 0.45), 0 12px 24px rgba(0, 0, 0, 0.55)"
      : "0 3px 10px rgba(0, 0, 0, 0.14), 0 12px 24px rgba(0, 0, 0, 0.18)";

    const boxShadow = hovered
      ? `var(--shadow-card-hover, ${fallbackHover})`
      : `var(--shadow-card, ${fallbackBase})`;

    const mergedStyle = {
      ...style,
      boxShadow,
      transform: hovered ? "translateY(-2px)" : "translateY(0)",
      transition: "box-shadow 150ms ease, transform 150ms ease",
    } as React.CSSProperties;

    return (
      <div
        ref={ref}
        className={cn("rounded-2xl bg-card text-card-foreground border border-border/50 min-w-[240px] sm:min-w-[280px]", className)}
        style={mergedStyle}
        onMouseEnter={(e) => {
          setHovered(true);
          onMouseEnter?.(e);
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          onMouseLeave?.(e);
        }}
        {...props}
      />
    );
  },
);
Card.displayName = "Card";

const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex flex-col space-y-1.5 p-6", className)} {...props} />
  ),
);
CardHeader.displayName = "CardHeader";

const CardTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn("text-2xl font-semibold leading-none tracking-tight", className)} {...props} />
  ),
);
CardTitle.displayName = "CardTitle";

const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn("text-sm text-muted-foreground", className)} {...props} />
  ),
);
CardDescription.displayName = "CardDescription";

const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => <div ref={ref} className={cn("p-6 pt-0", className)} {...props} />,
);
CardContent.displayName = "CardContent";

const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn("flex items-center p-6 pt-0", className)} {...props} />
  ),
);
CardFooter.displayName = "CardFooter";

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent };
